
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Phone, Mail, ChevronRight } from 'lucide-react';
import { services, companyInfo } from '../data';
import ContactForm from '../components/ContactForm';

const ServiceDetail = () => {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="pt-24 md:pt-32 pb-20">
      {/* Breadcrumbs */}
      <div className="bg-gray-50 py-4 mb-12">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-green-600 transition-colors">Forside</Link>
            <ChevronRight size={14} />
            <span className="text-gray-500">Ydelser</span>
            <ChevronRight size={14} />
            <span className="text-gray-900 font-medium">{service.title}</span>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                {service.title}
              </h1>
              
              {service.image && service.image !== "" && (
                <div className="aspect-video w-full rounded-3xl overflow-hidden mb-10 shadow-2xl">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover"
                  />
                </div>
              )}

              <div className="prose prose-lg max-w-none text-gray-600 mb-12">
                <p className="text-xl leading-relaxed mb-8 text-gray-700">
                  {service.fullDescription}
                </p>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Hvad vi tilbyder inden for {service.title.toLowerCase()}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-12">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3 bg-white p-4 rounded-2xl border border-gray-100 shadow-sm">
                      <CheckCircle2 className="text-green-600 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-700 font-medium">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* Special Section for Hedge Trimming Packages */}
                {slug === 'haekkeklip' && (
                  <div className="mb-12 bg-white rounded-3xl border border-gray-100 p-6 md:p-8 shadow-lg">
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">Priser & Pakkeløsninger</h3>
                    <p className="text-gray-600 mb-6">
                      Vi tilbyder gennemskuelige priser og skræddersyede pakkeløsninger til hækkeklipning, så du får præcis den service, der passer til dit behov. Se vores pakker og priser herunder:
                    </p>
                    <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-md bg-gray-50">
                      <img 
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=90,format=auto/assets/8418e253-1d56-459d-9b64-299630f7d780/ha-kkeklip.jpg" 
                        alt="Pakkeløsninger og priser for hækkeklipning" 
                        className="w-full h-auto"
                      />
                    </div>
                  </div>
                )}

                {/* Gallery Section */}
                {service.gallery && service.gallery.filter(img => img && img !== "").length > 0 && (
                  <div className="mb-12">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Se vores arbejde med {service.title.toLowerCase()}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {service.gallery.filter(img => img && img !== "").map((img, idx) => (
                        <div key={idx} className="aspect-[4/3] rounded-3xl overflow-hidden shadow-lg group">
                          <img 
                            src={img} 
                            alt={`${service.title} galleri ${idx + 1}`} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-green-50 rounded-3xl p-8 border border-green-100">
                  <h3 className="text-2xl font-bold text-green-900 mb-4">Kvalitet og holdbarhed i fokus</h3>
                  <p className="text-green-800/80 mb-6">
                    Vi ved, at et anlægsprojekt er en investering i din ejendom. Derfor går vi aldrig på kompromis med hverken materialer eller udførelse. Vores mål er altid at levere et resultat, som du kan have glæde af i mange årtier.
                  </p>
                  <div className="flex justify-center sm:justify-start gap-4 flex-col sm:flex-row">
                    <Link 
                      to="/kontakt" 
                      className="inline-flex items-center justify-center gap-2 bg-green-600 text-white w-[289px] sm:w-fit px-6 py-3 rounded-xl font-bold hover:bg-green-700 transition-all shadow-lg shadow-[#1b4c2e]/20 text-sm"
                    >
                      Få et uforpligtende tilbud
                      <ArrowRight size={16} />
                    </Link>
                    <Link 
                      to="/galleri" 
                      className="inline-flex items-center justify-center gap-2 bg-white text-green-700 border border-green-200 w-[289px] sm:w-fit px-6 py-3 rounded-xl font-bold hover:bg-green-50 transition-all text-sm"
                    >
                      Se mere i galleriet
                    </Link>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-8">
            {/* Other Services Menu */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Vores ydelser</h3>
              <div className="flex flex-col gap-2">
                {services.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/ydelser/${s.slug}`}
                    className={`flex items-center justify-between p-4 rounded-2xl transition-all font-semibold ${
                      s.slug === slug 
                        ? 'bg-green-600 text-white' 
                        : 'bg-gray-50 text-gray-600 hover:bg-green-50 hover:text-green-700'
                    }`}
                  >
                    {s.title}
                    <ChevronRight size={18} opacity={s.slug === slug ? 1 : 0.5} />
                  </Link>
                ))}
              </div>
            </div>

            {/* Contact Box */}
            <div className="bg-[#1b4c2e] text-white rounded-3xl p-8 shadow-2xl relative overflow-hidden group/card">
              <div className="absolute top-0 right-0 p-4 opacity-10 group-hover/card:scale-110 transition-transform duration-500">
                <Phone size={120} />
              </div>
              <div className="relative z-10">
                <h3 className="text-2xl font-bold mb-4">Har du spørgsmål?</h3>
                <p className="text-gray-400 mb-8">
                  Ring eller skriv til os for en uforpligtende snak om dit næste projekt.
                </p>
                <div className="space-y-4">
                  <a 
                    href={`tel:${companyInfo.phone.replace(/\s/g, '')}`}
                    className="flex items-center gap-4 group/item"
                  >
                    <div className="p-3.5 flex-shrink-0 rounded-full bg-green-600 flex items-center justify-center group-hover/item:scale-110 transition-transform">
                      <Phone size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Ring til os</div>
                      <div className="font-bold">{companyInfo.phone}</div>
                    </div>
                  </a>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-center gap-4 group/item"
                  >
                    <div className="p-3.5 flex-shrink-0 rounded-full bg-gray-800 flex items-center justify-center group-hover/item:scale-110 transition-transform">
                      <Mail size={20} />
                    </div>
                    <div>
                      <div className="text-xs text-gray-400 font-bold uppercase tracking-wider">Send en mail</div>
                      <div className="text-sm font-bold truncate max-w-[180px] md:max-w-none">{companyInfo.email}</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Form */}
            <div className="bg-white rounded-3xl p-8 border border-gray-100 shadow-xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Bliv kontaktet</h3>
              <ContactForm compact />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default ServiceDetail;
